import {
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import LockIcon from '@material-ui/icons/Lock'
import React from 'react'

import { fetchApartmentPricing, updateApartmentPricing } from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'
import { formatAmount } from 'app/utils'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'

// 2x a week is exactly double the 1x price in every source sheet, and 2x is the
// maximum frequency. Only the 1x price is stored, entered, or shown — the
// booking preview derives the 2x figure when a family picks two timings.

// Local date, not `toISOString()`, which is UTC and hands back yesterday for
// anyone east of Greenwich for the first hours of the day — the backend
// rejects a past effective date, so that would be a rejected save at 3am IST.
const todayISO = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10)
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 6,
    color: theme.palette.text.secondary
  },
  meta: { marginBottom: theme.spacing(2), color: theme.palette.text.secondary },
  actions: { marginTop: theme.spacing(2) },
  editRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  programName: { minWidth: 140 },
  sectionHeading: {
    margin: theme.spacing(2, 0, 1),
    fontWeight: 600
  },
  tierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  addSelect: { minWidth: 200 },
  unpriced: { color: theme.palette.text.secondary, fontStyle: 'italic' },
  scheduled: { marginTop: theme.spacing(3) }
}))

const ApartmentPricing = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [config, setConfig] = React.useState(null)
  const [editOpen, setEditOpen] = React.useState(false)
  const [draft, setDraft] = React.useState([])
  const [tiers, setTiers] = React.useState([])
  const [effectiveFrom, setEffectiveFrom] = React.useState(todayISO())
  // Subjects the building does not run yet, minus the ones already pulled into
  // this draft — so the picker never offers the same subject twice.
  const [available, setAvailable] = React.useState([])
  const [toAdd, setToAdd] = React.useState('')

  React.useEffect(() => {
    let active = true
    setConfig(null)
    fetchApartmentPricing(apartment.id).then((res) => active && setConfig(res))
    return () => {
      active = false
    }
  }, [apartment.id])

  if (!config) return <Loading />

  // canEdit is computed by the backend from the logged-in user's allowlist
  // membership; the PATCH endpoint enforces the same gate server-side.
  const editable = Boolean(config.canEdit)
  const unpricedPrograms = config.unpricedPrograms || []
  const availableSubjects = config.availableSubjects || []
  const scheduledRevisions = config.scheduledRevisions || []
  const hasPricing = config.programs.length > 0
  // A building stood up on the Complexes page has no programmes at all, so
  // there is nothing to price until one is added here.
  const isEmptyBuilding = !hasPricing && unpricedPrograms.length === 0

  const openEdit = () => {
    setDraft([
      ...config.programs.map((p) => ({
        program: p.program,
        onceWeekly: p.onceWeekly,
        kitCharge: p.kitCharge || ''
      })),
      // Programmes the building runs but has never priced. They arrive blank
      // rather than as a zero, because zero is a real price and "not set" is
      // not.
      ...unpricedPrograms.map((p) => ({
        program: p.program,
        onceWeekly: '',
        kitCharge: ''
      }))
    ])
    setTiers(config.discountTiers.map((t) => ({ ...t })))
    setEffectiveFrom(todayISO())
    setAvailable(availableSubjects)
    setToAdd('')
    setEditOpen(true)
  }

  const setPrice = (index, key, value) =>
    setDraft((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, [key]: value === '' ? '' : Number(value) } : p
      )
    )

  const addProgram = () => {
    if (!toAdd) return
    setDraft((prev) => [
      ...prev,
      { program: toAdd, onceWeekly: '', kitCharge: '' }
    ])
    setAvailable((prev) => prev.filter((s) => s !== toAdd))
    setToAdd('')
  }

  const removeProgram = (index) =>
    setDraft((prev) => {
      const row = prev[index]
      // Only a subject added in this draft goes back on the picker; one the
      // building already runs is not un-offered by dropping it from a revision.
      if (row && availableSubjects.includes(row.program)) {
        setAvailable((list) =>
          list.includes(row.program) ? list : [...list, row.program]
        )
      }
      return prev.filter((_, i) => i !== index)
    })

  const setTier = (index, key, value) =>
    setTiers((prev) =>
      prev.map((t, i) =>
        i === index ? { ...t, [key]: value === '' ? '' : Number(value) } : t
      )
    )

  const addTier = () =>
    setTiers((prev) => [...prev, { minEnrolments: '', discountPercent: '' }])

  const removeTier = (index) =>
    setTiers((prev) => prev.filter((_, i) => i !== index))

  const handleSave = async () => {
    // A blank price means "not priced in this revision", so the row is left
    // out entirely rather than written as a zero — which would be a free seat.
    const priced = draft.filter(
      (p) => p.onceWeekly !== '' && p.onceWeekly !== null
    )
    if (priced.length === 0) {
      eventAlert('Enter a price for at least one subject', constants.ERROR)
      return
    }
    try {
      const updated = await updateApartmentPricing(apartment.id, {
        programs: priced.map((p) => ({
          program: p.program,
          onceWeekly: p.onceWeekly,
          // Blank means "no kit"; the backend stores that as NULL and never
          // charges for it.
          kitCharge: p.kitCharge === '' ? null : p.kitCharge
        })),
        discountTiers: tiers
          .filter((t) => t.minEnrolments !== '' && t.discountPercent !== '')
          .map((t) => ({
            minEnrolments: t.minEnrolments,
            discountPercent: t.discountPercent
          })),
        effective_from: effectiveFrom
      })
      setConfig(updated)
      eventAlert(
        effectiveFrom > todayISO()
          ? `Pricing saved — it takes over on ${effectiveFrom}`
          : 'Pricing updated',
        constants.SUCCESS
      )
      setEditOpen(false)
    } catch (error) {
      // request.js already surfaces a toast (e.g. 403 if not allowlisted).
    }
  }

  return (
    <div className={classes.root}>
      <ApartmentPageHeader title="Pricing" />

      <div className={classes.banner}>
        <LockIcon fontSize="small" />
        {editable ? (
          <span>
            You are on the pricing-editor allowlist — editing enabled.
          </span>
        ) : (
          <span>
            Read-only. Pricing can only be edited by allowlisted users.
          </span>
        )}
      </div>

      {!hasPricing ? (
        <Typography variant="body2" className={classes.meta}>
          {isEmptyBuilding
            ? 'No subjects set up for this building yet. Adding the first prices below is what creates them — batches can then be scheduled against them.'
            : 'No pricing configured for this building yet.'}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" className={classes.meta}>
            GST {config.gstPercent}%{' '}
            {config.gstInclusive ? 'inclusive' : 'extra'} · Discount tiers:{' '}
            {config.discountTiers.length === 0
              ? 'none'
              : config.discountTiers
                  .map((t) => `${t.minEnrolments}+ → ${t.discountPercent}%`)
                  .join(', ')}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell align="right">1x / week</TableCell>
                  <TableCell align="right">Kit charge</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.programs.map((p) => (
                  <TableRow key={p.program}>
                    <TableCell>{p.program}</TableCell>
                    <TableCell align="right">
                      {formatAmount(p.onceWeekly)}
                    </TableCell>
                    <TableCell align="right">
                      {p.kitCharge ? formatAmount(p.kitCharge) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* A programme with batches but no price bills nothing, so it
                    is listed rather than hidden. */}
                {unpricedPrograms.map((p) => (
                  <TableRow key={p.program}>
                    <TableCell className={classes.unpriced}>
                      {p.program}
                    </TableCell>
                    <TableCell align="right" className={classes.unpriced}>
                      not priced
                    </TableCell>
                    <TableCell align="right" className={classes.unpriced}>
                      —
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {scheduledRevisions.length > 0 && (
        <div className={classes.scheduled}>
          <Typography variant="subtitle2" className={classes.sectionHeading}>
            Scheduled changes
          </Typography>
          {scheduledRevisions.map((rev) => (
            <Typography key={rev.from} variant="body2" className={classes.meta}>
              From {rev.from}:{' '}
              {rev.programs
                .map(
                  (p) =>
                    `${p.program} ${formatAmount(p.onceWeekly)}` +
                    (p.kitCharge ? ` + kit ${formatAmount(p.kitCharge)}` : '')
                )
                .join(' · ')}
            </Typography>
          ))}
        </div>
      )}

      <div className={classes.actions}>
        {editable ? (
          <Button variant="contained" color="primary" onClick={openEdit}>
            {hasPricing ? 'Edit pricing' : 'Set pricing'}
          </Button>
        ) : (
          <Chip icon={<LockIcon />} label="Editing locked" />
        )}
      </div>

      <ModalForm
        isModalOpen={editOpen}
        onModalClose={() => setEditOpen(false)}
        modalTitle={hasPricing ? 'Edit Pricing' : 'Set Pricing'}
        enableSave
        buttonLabel="Save"
        onSaveClick={handleSave}
        maxWidth="md"
      >
        <div>
          <Typography variant="body2" className={classes.hint}>
            2x a week is always double the 1x price, so it is not editable.
            Leave the kit charge blank for subjects with no kit — a kit is
            billed once per level, per child, per subject. Leave the 1x price
            blank to leave that subject out of this revision.
          </Typography>

          <TextField
            type="date"
            label="Takes effect from"
            size="small"
            variant="outlined"
            value={effectiveFrom}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
          <Typography variant="body2" className={classes.hint}>
            Saving never overwrites the current prices — it adds a revision that
            takes over on this date, so invoices already raised stay explicable.
            Date it ahead for a price change you know is coming.
          </Typography>

          {draft.map((p, i) => (
            <div className={classes.editRow} key={p.program}>
              <span className={classes.programName}>{p.program}</span>
              <TextField
                type="number"
                label="1x / week"
                size="small"
                variant="outlined"
                value={p.onceWeekly}
                onChange={(e) => setPrice(i, 'onceWeekly', e.target.value)}
              />
              <TextField
                type="number"
                label="Kit charge"
                size="small"
                variant="outlined"
                placeholder="No kit"
                value={p.kitCharge || ''}
                onChange={(e) => setPrice(i, 'kitCharge', e.target.value)}
              />
              <IconButton size="small" onClick={() => removeProgram(i)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}

          {available.length > 0 && (
            <div className={classes.addRow}>
              <TextField
                select
                label="Add a subject"
                size="small"
                variant="outlined"
                className={classes.addSelect}
                value={toAdd}
                onChange={(e) => setToAdd(e.target.value)}
              >
                {available.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addProgram}
                disabled={!toAdd}
              >
                Add
              </Button>
            </div>
          )}

          <Divider />
          <Typography variant="subtitle2" className={classes.sectionHeading}>
            Family discount tiers
          </Typography>
          <Typography variant="body2" className={classes.hint}>
            Applied on the whole family&apos;s monthly total, by number of
            weekly sessions booked together. Kits are never discounted.
          </Typography>

          {tiers.map((t, i) => (
            // Tiers have no stable id and can be reordered by editing, so the
            // index is the only key available.
            // eslint-disable-next-line react/no-array-index-key
            <div className={classes.tierRow} key={i}>
              <TextField
                type="number"
                label="Sessions (min)"
                size="small"
                variant="outlined"
                value={t.minEnrolments}
                onChange={(e) => setTier(i, 'minEnrolments', e.target.value)}
              />
              <TextField
                type="number"
                label="Discount %"
                size="small"
                variant="outlined"
                value={t.discountPercent}
                onChange={(e) => setTier(i, 'discountPercent', e.target.value)}
              />
              <IconButton size="small" onClick={() => removeTier(i)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addTier}>
            Add tier
          </Button>
        </div>
      </ModalForm>
    </div>
  )
}

export default ApartmentPricing
